<template>
  <section class="balance-exchange-rate">
    <h2 class="section-title">{{ $t('dashboard.my_assets') }}</h2>

    <div
      ref="balanceCard"
      class="balance-card"
      :class="{
        'balance-card--compact': defaultVisibleCount === 4,
        'balance-card--three-columns': defaultVisibleCount === 3
      }"
    >
      <el-skeleton style="width: 100%" :loading="loading" animated>
        <div>
          <div class="balance-card__header">
            <div>
              <h3 class="card-title">{{ $t('dashboard.account_balance') }}</h3>
              <div class="total-balance">
                <span class="total-balance__amount">≈ {{ totalBalance | toDivideWithDot }}</span>
                <span class="total-balance__currency">USD</span>
              </div>
              <p class="card-tip">{{ $t('dashboard.balance_exchange_rate_tip') }}</p>
            </div>

            <el-button v-if="$store.getters.authorities.includes('AUTH_CODE_BALANCE')" type="primary" class="withdraw-button" @click="$refs.withdrawModalRef.changModalStatus(true)">
              {{ $t('dashboard.withdraw_now') }}
            </el-button>
          </div>

          <div class="currency-list">
            <div
              v-for="item in visibleBalances"
              :key="item.currency"
              class="currency-item"
            >
              <img class="currency-item__flag" :src="item.flag" alt="" aria-hidden="true">
              <span class="currency-item__code">{{ item.currency }}</span>
              <span class="currency-item__amount">{{ item.amount | toDivideWithDot }}</span>
            </div>
          </div>

          <button
            v-if="balances.length > defaultVisibleCount"
            type="button"
            class="expand-button"
            :aria-expanded="expanded"
            @click="expanded = !expanded"
          >
            {{ expanded ? $t('dashboard.collapse') : $t('dashboard.expand_more') }}
            <i :class="expanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
          </button>
        </div>
      </el-skeleton>
    </div>

    <h2 class="section-title exchange-title">{{ $t('dashboard.reference_exchange_rate') }}</h2>

    <div class="exchange-card">
      <div class="exchange-row">
        <el-input
          v-model="sourceAmount"
          class="amount-input"
          @input="handleSourceInput"
        >
          <el-select
            slot="append"
            v-model="sourceCurrency"
            class="currency-select"
            disabled
          >
            <el-option
              v-for="item in sourceCurrencyOptions"
              :key="item.value"
              :value="item.value"
            >
              <span class="select-option">
                <img class="select-option__flag" :src="item.flag" alt="">
                {{ item.value }}
              </span>
            </el-option>
          </el-select>
        </el-input>

        <button
          type="button"
          class="exchange-icon"
          tabindex="-1"
        >→</button>

        <el-input
          v-model="targetAmount"
          class="amount-input"
          readonly
        >
          <el-select
            slot="append"
            v-model="targetCurrency"
            class="currency-select"
            @change="handleTargetCurrencyChange"
          >
            <el-option
              v-for="item in currencyOptions"
              :key="item.value"
              :value="item.value"
            >
              <span class="select-option">
                <img class="select-option__flag" :src="item.flag" alt="">
                {{ item.value }}
              </span>
            </el-option>
          </el-select>
        </el-input>
      </div>

      <p v-if="targetCurrency && exchangeRate" class="rate-info">1 {{ sourceCurrency }} = {{ exchangeRate }} {{ targetCurrency }} · {{ $t('dashboard.updated_at') }} {{ updatedAt }}</p>
      <p class="rate-tip">{{ $t('dashboard.exchange_rate_disclaimer') }}</p>
    </div>

    <MultiMchWithdrawModal
      ref="withdrawModalRef"
    />
  </section>
</template>

<script>
import { fetchMchExchangeRate, fetchMchMultiInfo } from '@/api/dashboard/dashboard'
import math from '@/utils/math'

const EXCHANGE_RATE_POLL_INTERVAL = 5 * 60 * 1000
const DEFAULT_CURRENCY_FLAG = require('@/assets/flags/default.svg')
const CURRENCY_FLAG_MAP = {
  USD: require('@/assets/flags/hk.svg'),
  IDR: require('@/assets/flags/id.svg'),
  MYR: require('@/assets/flags/my.svg'),
  MXN: require('@/assets/flags/mx.svg'),
  BRL: require('@/assets/flags/br.svg'),
  PHP: require('@/assets/flags/ph.svg'),
  THB: require('@/assets/flags/th.svg'),
  AED: require('@/assets/flags/ae.svg'),
  SAR: require('@/assets/flags/sa.svg'),
  QAR: require('@/assets/flags/qa.svg'),
  KWD: require('@/assets/flags/kw.svg'),
  OMR: require('@/assets/flags/om.svg'),
  BHD: require('@/assets/flags/bh.svg'),
  RUB: require('@/assets/flags/ru.svg'),
  HKD: require('@/assets/flags/hk.svg'),
  SGD: require('@/assets/flags/sg.svg'),
  EUR: require('@/assets/flags/eu.svg'),
  GBP: require('@/assets/flags/gb.svg'),
  CNY: require('@/assets/flags/cn.svg'),
  JPY: require('@/assets/flags/jp.svg')
}

export default {
  name: 'MyBalanceAndExchangeRate',
  components: {
    MultiMchWithdrawModal: () => import('@/views/paycools/data/components/Modal/MultiMchWithdrawModal.vue')
  },
  data() {
    return {
      defaultVisibleCount: 5,
      expanded: false,
      balanceCardResizeObserver: null,
      exchangeRateTimer: null,
      sourceAmount: '1',
      targetAmount: '',
      sourceCurrency: 'USD',
      targetCurrency: undefined,
      sourceCurrencyOptions: [
        { flag: require('@/assets/flags/us.svg'), value: 'USD' }
      ],
      totalBalance: 0,
      balances: [],
      exchangeRate: null,
      updatedAt: '',
      loading: false
    }
  },
  computed: {
    currencyOptions() {
      return this.$store.state.page.countryArr.filter(item => item.currency !== this.sourceCurrency).map(item => ({
        flag: CURRENCY_FLAG_MAP[item.currency] || DEFAULT_CURRENCY_FLAG,
        value: item.currency
      }))
    },
    visibleBalances() {
      return this.expanded ? this.balances : this.balances.slice(0, this.defaultVisibleCount)
    },
    displayRate() {
      return this.exchangeRate.toFixed(2)
    }
  },
  watch: {
    currencyOptions: {
      immediate: true,
      handler(options) {
        if (!options.length || options.some(item => item.value === this.targetCurrency)) {
          return
        }
        this.targetCurrency = options[0].value
        this.handleTargetCurrencyChange()
      }
    }
  },
  mounted() {
    this.updateVisibleCount()
    this.getMchMultiInfo()

    if (window.ResizeObserver) {
      this.balanceCardResizeObserver = new window.ResizeObserver(entries => {
        if (entries.length) {
          this.updateVisibleCount(entries[0].contentRect.width)
        }
      })
      this.balanceCardResizeObserver.observe(this.$refs.balanceCard)
    } else {
      window.addEventListener('resize', this.updateVisibleCount)
    }
  },
  beforeDestroy() {
    this.clearExchangeRateTimer()
    if (this.balanceCardResizeObserver) {
      this.balanceCardResizeObserver.disconnect()
    } else {
      window.removeEventListener('resize', this.updateVisibleCount)
    }
  },
  methods: {
    getMchMultiInfo() {
      this.loading = true
      return fetchMchMultiInfo().then(res => {
        const {
          mch_info_resp_list = [],
          total_balance = 0
        } = res.data || {}

        this.totalBalance = total_balance
        this.balances = mch_info_resp_list.map(item => {
          const { merchant = {}, balance = {}} = item
          const currency = merchant.currency
          return {
            currency,
            amount: balance.balance || 0,
            flag: CURRENCY_FLAG_MAP[currency] || DEFAULT_CURRENCY_FLAG
          }
        }).filter(item => item.amount > 0)
      }).finally(() => {
        this.loading = false
      })
    },
    updateVisibleCount(width) {
      const cardWidth = typeof width === 'number'
        ? width
        : this.$refs.balanceCard.getBoundingClientRect().width
      const nextVisibleCount = cardWidth < 820 ? 3 : cardWidth < 1000 ? 4 : 5

      if (nextVisibleCount !== this.defaultVisibleCount) {
        this.defaultVisibleCount = nextVisibleCount
        this.expanded = false
      }
    },
    parseAmount(value) {
      const amount = Number(String(value).replace(/,/g, ''))
      return Number.isFinite(amount) ? amount : 0
    },
    formatAmount(value) {
      return value.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    formatSourceAmount(value) {
      const numStr = String(value).replace(/,/g, '').match(/^\d*(\.\d{0,2})?/)
      const matched = numStr ? numStr[0] : ''
      const [intPart, decPart] = matched.split('.')
      const formattedInt = intPart ? intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''
      return decPart !== undefined ? `${formattedInt}.${decPart}` : formattedInt
    },
    handleSourceInput(value) {
      this.sourceAmount = this.formatSourceAmount(value)
      if (!this.targetCurrency || !this.exchangeRate) {
        this.targetAmount = ''
        return
      }
      const targetAmount = math.multiply(this.parseAmount(this.sourceAmount), this.exchangeRate)
      this.targetAmount = this.formatAmount(Number(targetAmount.valueOf()))
    },
    handleTargetCurrencyChange() {
      this.clearExchangeRateTimer()
      this.getMchExchangeRate(true)
    },
    getMchExchangeRate(startPolling = false) {
      const quoteCurrency = this.targetCurrency
      if (!quoteCurrency) {
        return Promise.resolve()
      }
      const params = {
        base_currency: 'USD',
        quote_currency: quoteCurrency
      }
      return fetchMchExchangeRate(params).then(res => {
        if (quoteCurrency !== this.targetCurrency) {
          return
        }
        const { quoted_at, mch_rate } = res.data || {}
        const exchangeRate = Number(mch_rate)
        if (!Number.isFinite(exchangeRate)) {
          return
        }
        this.exchangeRate = exchangeRate
        this.updatedAt = quoted_at ? String(quoted_at).replace('T', ' ') : ''
        this.handleSourceInput(this.sourceAmount)
        if (startPolling) {
          this.startExchangeRatePolling()
        }
      })
    },
    startExchangeRatePolling() {
      this.clearExchangeRateTimer()
      this.exchangeRateTimer = window.setInterval(() => {
        this.getMchExchangeRate()
      }, EXCHANGE_RATE_POLL_INTERVAL)
    },
    clearExchangeRateTimer() {
      if (this.exchangeRateTimer) {
        window.clearInterval(this.exchangeRateTimer)
        this.exchangeRateTimer = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.balance-exchange-rate {
  padding: 18px;
  color: #101426;
  background: #f5f6fa;
}

.section-title {
  margin: 0 0 14px;
  color: #354052;
  font-size: 12.6px;
  font-weight: 600;
  line-height: 25px;
}

.balance-card,
.exchange-card {
  box-sizing: border-box;
  background: #fff;
  border: 1px solid #eef0f4;
  box-shadow: 0 2px 5px rgba(26, 39, 74, 0.02);
}

.balance-card {
  padding: 30px 30px 24px;
  border-radius: 24px;
}

.balance-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-right: 80px;
}

.card-title {
  margin: 0 0 5px;
  font-size: 16.8px;
  font-weight: 600;
  line-height: 34px;
}

.total-balance {
  display: flex;
  align-items: baseline;
  gap: 18px;
}

.total-balance__amount {
  font-size: 30.8px;
  font-weight: 700;
  letter-spacing: 0.5px;
  line-height: 54px;
}

.total-balance__currency {
  color: #7b8495;
  font-size: 15.4px;
  font-weight: 600;
}

.card-tip,
.rate-info,
.rate-tip {
  color: #a3acbc;
  font-size: 11.2px;
  line-height: 24px;
}

.card-tip {
  margin: 0 0 20px;
}

.withdraw-button.el-button {
  width: 120.6px;
  height: 38.5px;
  padding: 0 10px;
  border: 0;
  border-radius: 9.1px;
  background: #2f7df4;
  box-shadow: 0 6px 14px rgba(47, 125, 244, 0.28);
  font-size: 14.7px;
  font-weight: 600;
}

.currency-list {
  display: grid;
  grid-template-columns: repeat(5, minmax(170px, 1fr));
  gap: 16px;
}

.currency-item {
  display: flex;
  align-items: center;
  min-width: 0;
  height: 40px;
  padding: 0 10px;
  overflow: hidden;
  border-radius: 12px;
  background: #f7f8fa;
  font-size: 12.6px;
  font-weight: 600;
}

.currency-item__flag {
  width: 22px;
  height: 16.5px;
  margin-right: 10px;
  border-radius: 2px;
  object-fit: cover;
}

.currency-item__code {
  margin-right: 10px;
  white-space: nowrap;
}

.currency-item__amount {
  margin-left: auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.select-option {
  display: inline-flex;
  align-items: center;
}

.select-option__flag {
  width: 18px;
  height: 13.5px;
  margin-right: 6px;
  border-radius: 2px;
}

.expand-button {
  display: flex;
  align-items: center;
  gap: 9px;
  height: 30.1px;
  margin: 29px auto 0;
  padding: 0 19.6px;
  border: 0;
  border-radius: 15.4px;
  color: #667084;
  background: #f1f3f6;
  font-size: 11.2px;
  font-weight: 600;
  cursor: pointer;
}

.exchange-title {
  margin-top: 30px;
}

.exchange-card {
  padding: 26px 34px 24px;
  border-radius: 18px;
}

.exchange-row {
  display: flex;
  align-items: center;
  gap: 18px;
}

.amount-input {
  flex: 1;
}

.amount-input ::v-deep .el-input__inner {
  height: 50px;
  padding: 0 24px;
  border-color: #e4e7ed;
  border-radius: 15px 0 0 15px;
  color: #182033;
  font-size: 23.8px;
  font-weight: 500;
}

.amount-input ::v-deep .el-input-group__append {
  width: 110px;
  padding: 0;
  border-color: #e4e7ed;
  border-radius: 0 15px 15px 0;
  color: #232b3d;
  background: #f8f9fb;
}

.currency-select {
  width: 110px;
  margin: 0;
}

.amount-input ::v-deep .currency-select .el-input__inner {
  height: 48px;
  padding: 0 42px 0 20px;
  border: 0;
  border-radius: 0 15px 15px 0;
  color: #232b3d;
  background: #f8f9fb;
  font-family: "Apple Color Emoji", "Segoe UI Emoji", sans-serif;
  font-size: 12.6px;
  font-weight: 600;
}

.amount-input ::v-deep .currency-select .el-input__suffix {
  right: 14px;
}

.amount-input ::v-deep .currency-select .el-select__caret {
  color: #8790a1;
  font-size: 9.8px;
}

.exchange-icon {
  flex: 0 0 35px;
  height: 35px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  color: #2f7df4;
  background: #eaf2ff;
  font-size: 17.5px;
  line-height: 35px;
  cursor: default;
}

.rate-info {
  margin: 17px 0 7px;
}

.rate-tip {
  margin: 0;
}

@media (max-width: 1200px) {

  .currency-list {
    grid-template-columns: repeat(3, minmax(220px, 1fr));
  }
}

.balance-card.balance-card--compact .currency-list {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.balance-card.balance-card--three-columns .currency-list {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}
</style>
